import React, { useMemo, useState } from 'react';

import type { AutocompleteProps } from '@mui/material';
import { Autocomplete, Typography } from '@mui/material';
import type { AutocompleteChangeReason } from '@mui/material/useAutocomplete';
import { matchSorter } from 'match-sorter';
import { AlineAutocompleteInput, AlineAvatar, Spacer } from 'src/components';
import type { Apollo } from 'src/gql/generated';
import { always } from 'src/helpers';
import { useGetRequesterUserDefinedRoles } from 'src/hooks';
import { roleAvatarColor } from 'src/theme';
import { makeStyles } from 'tss-react/mui';

type ExistingRoleOption = Pick<Apollo.UserDefinedRole, '__typename' | 'id' | 'roleName'>;
interface NewRoleOption {
  __typename: 'NewRole';
  roleName: string;
}

export type RoleOption = ExistingRoleOption | NewRoleOption;

type CustomAutocompleteProps = AutocompleteProps<RoleOption, false, false, false>;
type AccountsRolesAutocompleteValue = RoleOption | null;

const useStyles = makeStyles()(theme => ({
  autocompleteOption: {
    padding: theme.spacing(1),
  },
}));

export interface RolesAutoCompleteProps {
  onChange: (
    event: React.ChangeEvent<unknown>,
    value: AccountsRolesAutocompleteValue,
    reason: AutocompleteChangeReason
  ) => void;
  value: AccountsRolesAutocompleteValue;
  blurOnSelect?: CustomAutocompleteProps['blurOnSelect'];
  className?: string;
  clearInputOnBlur?: boolean;
  clearOnBlur?: boolean;
  dataCy?: string;
  disabled?: boolean;
  filterPred?: (option: RoleOption) => boolean;
  filterSelectedOptions?: boolean;
  fullWidth?: boolean;
  getIsOptionDisabled?: (option: RoleOption) => boolean;
  placeholder?: string;
}

export function RolesAutoComplete(props: RolesAutoCompleteProps): JSX.Element | null {
  const {
    blurOnSelect = true,
    className,
    clearInputOnBlur = false,
    clearOnBlur = false,
    dataCy,
    disabled,
    filterPred = always,
    filterSelectedOptions = false,
    fullWidth = true,
    getIsOptionDisabled,
    onChange,
    placeholder = 'Search roles...',
    value,
  } = props;

  const { classes } = useStyles();
  const [open, setOpen] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>('');
  const { loading, refetch: refetchRoles, userDefinedRoles } = useGetRequesterUserDefinedRoles();
  const options = useMemo((): RoleOption[] => {
    const filteredRoles = userDefinedRoles.filter(filterPred);
    return filteredRoles.sort((a, b) => a.roleName.localeCompare(b.roleName));
  }, [userDefinedRoles, filterPred]);

  return (
    <Autocomplete<RoleOption, false, false, false>
      openOnFocus
      blurOnSelect={blurOnSelect}
      className={className}
      classes={{ option: classes.autocompleteOption }}
      clearOnBlur={clearOnBlur}
      data-cy={dataCy}
      disabled={disabled}
      filterOptions={(options, filterOptionsState) => {
        const roleOptionKeys: (keyof RoleOption)[] = ['roleName'];
        const sortedRoleOptions = matchSorter<RoleOption>(options, inputValue, {
          keys: roleOptionKeys,
        });
        const roleAlreadyExists = options.some(
          option => option.roleName.toLowerCase() === filterOptionsState.inputValue.toLowerCase()
        );

        return filterOptionsState.inputValue && !roleAlreadyExists
          ? [
              ...sortedRoleOptions,
              {
                __typename: 'NewRole',
                roleName: filterOptionsState.inputValue,
              } satisfies NewRoleOption,
            ]
          : sortedRoleOptions;
      }}
      filterSelectedOptions={filterSelectedOptions}
      fullWidth={fullWidth}
      getOptionDisabled={getIsOptionDisabled}
      getOptionLabel={option => option.roleName}
      inputValue={inputValue}
      isOptionEqualToValue={(option, value) => {
        return 'id' in option && 'id' in value && option.id === value.id;
      }}
      loading={loading}
      open={open}
      options={options}
      renderInput={params => <AlineAutocompleteInput placeholder={placeholder} {...params} />}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;

        return (
          <li key={key} {...optionProps}>
            <RoleOption option={option} />
          </li>
        );
      }}
      slotProps={{
        listbox: {
          'data-cy': 'roles-autocomplete-options',
        },
      }}
      value={value}
      onBlur={() => clearInputOnBlur && setInputValue('')}
      onChange={onChange}
      onClose={() => setOpen(false)}
      onInputChange={(_, inputValue) => setInputValue(inputValue)}
      onOpen={async () => {
        setOpen(true);
        await refetchRoles();
      }}
    />
  );
}

const useOptionStyles = makeStyles()(theme => ({
  newRole: {
    ...theme.appTypography.buttonSmall,
    color: theme.appTypography.buttonSmall.color,
    whiteSpace: 'nowrap',
  },
  option: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    width: '100%',
  },
}));

interface RoleOptionProps {
  option: RoleOption;
}

function RoleOption({ option }: RoleOptionProps): JSX.Element {
  const { classes } = useOptionStyles();

  switch (option.__typename) {
    case 'NewRole': {
      return (
        <div className={classes.option}>
          <AlineAvatar
            avatarInfo={option.roleName.charAt(0).toUpperCase()}
            styles={{
              backgroundColor: roleAvatarColor,
              size: 'lg',
            }}
          />
          <Typography noWrap>{option.roleName}</Typography>
          <Spacer />
          <Typography className={classes.newRole}>Create role</Typography>
        </div>
      );
    }
    default: {
      return (
        <div className={classes.option}>
          <AlineAvatar
            avatarInfo={option}
            styles={{ backgroundColor: roleAvatarColor, size: 'lg' }}
          />
          <Typography noWrap>{option.roleName}</Typography>
        </div>
      );
    }
  }
}
